import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { createRulesApi } from '../../flux/facades/rulesApi';
import { useAppSelector } from '../../flux/stores/hooks';
import { useApiAccessToken } from '../../utils/useApiAccessToken';
import { apiErrorDetails, detectionLabel, errorMessage } from '../Rules/ruleHelpers';

// Activating and deactivating rules for the organization. Rule definitions
// themselves (create, edit, delete) live on the standalone Rules page — this
// tab only turns existing rules on or off.
export const OrganizationRules = () => {
    const { getAccessToken } = useApiAccessToken();
    const rulesApi = useMemo(() => createRulesApi(getAccessToken), [getAccessToken]);
    const currentUser = useAppSelector((state) => state.user.current.data);
    const isAdmin = currentUser?.org_role === 'admin';

    const [rules, setRules] = useState([]);
    const [activeRuleIds, setActiveRuleIds] = useState(() => new Set());
    const [isLoading, setIsLoading] = useState(true);
    const [pageError, setPageError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [pendingRuleId, setPendingRuleId] = useState(null);
    const [pickerOpen, setPickerOpen] = useState(false);

    const loadRules = useCallback(async () => {
        setIsLoading(true);
        setPageError(null);
        try {
            const [allRules, activeRules] = await Promise.all([
                rulesApi.getRules(),
                rulesApi.getActiveRules(),
            ]);
            setRules(allRules);
            setActiveRuleIds(new Set(activeRules.map((rule) => rule.object_id)));
        } catch (error) {
            setPageError(errorMessage(error, 'load'));
        } finally {
            setIsLoading(false);
        }
    }, [rulesApi]);

    useEffect(() => {
        loadRules();
    }, [loadRules]);

    const activeRules = rules.filter((rule) => activeRuleIds.has(rule.object_id));
    const inactiveRules = rules.filter((rule) => !activeRuleIds.has(rule.object_id));
    // Only rules that support inline evaluation can run as an org-wide check.
    const activatableRules = inactiveRules.filter((rule) => rule.supports_inline);

    const activate = async (rule) => {
        setPendingRuleId(rule.object_id);
        setNotice(null);
        try {
            await rulesApi.activateRule(rule.object_id);
            setActiveRuleIds((current) => new Set(current).add(rule.object_id));
            setNotice({ type: 'success', message: `${rule.name} activated.` });
        } catch (error) {
            if (apiErrorDetails(error).status === 404) await loadRules();
            else setPageError(errorMessage(error, 'activate'));
        } finally {
            setPendingRuleId(null);
        }
    };

    const deactivate = async (rule) => {
        setPendingRuleId(rule.object_id);
        setNotice(null);
        try {
            await rulesApi.deactivateRule(rule.object_id);
            setActiveRuleIds((current) => {
                const next = new Set(current);
                next.delete(rule.object_id);
                return next;
            });
            setNotice({ type: 'success', message: `${rule.name} deactivated.` });
        } catch (error) {
            if (apiErrorDetails(error).status === 404) await loadRules();
            else setPageError(errorMessage(error, 'deactivate'));
        } finally {
            setPendingRuleId(null);
        }
    };

    return (
        <div>
            <div className="rules-header">
                <div>
                    <h2 className="pw-page-title">Active rules</h2>
                    <p className="rules-page-subtitle">Rules currently enforced across this organization.</p>
                </div>
                {isAdmin && (
                    <button type="button" className="rules-btn-primary" onClick={() => { setNotice(null); setPickerOpen(true); }}>
                        + Activate a rule
                    </button>
                )}
            </div>

            {notice && <div className={`rules-alert rules-alert-${notice.type}`} role="status">{notice.message}</div>}
            {pageError && (
                <div className="rules-alert rules-alert-error" role="alert">
                    <span>{pageError}</span>
                    <button type="button" className="rules-btn-ghost" onClick={loadRules}>Retry</button>
                </div>
            )}

            {isLoading ? (
                <p className="projects-loading">Loading active rules…</p>
            ) : activeRules.length === 0 ? (
                <div className="rules-empty"><p>No active rules yet.</p></div>
            ) : (
                <div className="rules-table-wrapper">
                    <table className="rules-table">
                        <thead>
                            <tr>
                                <th className="rules-th">Name</th>
                                <th className="rules-th">First entity</th>
                                <th className="rules-th">Second entity</th>
                                <th className="rules-th">Effect</th>
                                <th className="rules-th">Detection</th>
                                {isAdmin && <th className="rules-th">Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {activeRules.map((rule) => (
                                <tr key={rule.object_id} className="rules-tr">
                                    <td className="rules-td rules-td-name">{rule.name}</td>
                                    <td className="rules-td">{rule.definition.firstEntity.entityType}</td>
                                    <td className="rules-td">{rule.definition.secondEntity?.entityType ?? '—'}</td>
                                    <td className="rules-td"><span className={`rules-effect rules-effect-${rule.effect}`}>{rule.effect}</span></td>
                                    <td className="rules-td rules-detection-cell">{detectionLabel(rule)}</td>
                                    {isAdmin && (
                                        <td className="rules-td">
                                            <button
                                                type="button"
                                                className="rules-action-button rules-action-danger"
                                                disabled={pendingRuleId === rule.object_id}
                                                onClick={() => deactivate(rule)}
                                            >
                                                {pendingRuleId === rule.object_id ? 'Deactivating…' : 'Deactivate'}
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {pickerOpen && (
                <div className="members-modal-backdrop" role="presentation">
                    <div className="members-modal members-modal-wide" role="dialog" aria-modal="true" aria-labelledby="activate-rule-title">
                        <h2 id="activate-rule-title">Activate a rule</h2>
                        <div className="members-modal-body">
                            {activatableRules.length === 0 ? (
                                <p>
                                    {rules.length === 0
                                        ? <>No rules exist yet. <Link to="/rules">Create one</Link> first.</>
                                        : inactiveRules.length === 0
                                            ? 'Every rule is already active.'
                                            : "The remaining rules don't support inline evaluation and can't be activated here."}
                                </p>
                            ) : (
                                <div className="rules-table-wrapper">
                                    <table className="rules-table">
                                        <thead>
                                            <tr>
                                                <th className="rules-th">Name</th>
                                                <th className="rules-th">Effect</th>
                                                <th className="rules-th">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {activatableRules.map((rule) => (
                                                <tr key={rule.object_id} className="rules-tr">
                                                    <td className="rules-td rules-td-name">{rule.name}</td>
                                                    <td className="rules-td"><span className={`rules-effect rules-effect-${rule.effect}`}>{rule.effect}</span></td>
                                                    <td className="rules-td">
                                                        <button
                                                            type="button"
                                                            className="rules-action-button"
                                                            disabled={pendingRuleId === rule.object_id}
                                                            onClick={() => activate(rule)}
                                                        >
                                                            {pendingRuleId === rule.object_id ? 'Activating…' : 'Activate'}
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                        <div className="pw-actions">
                            <button type="button" className="rules-btn-ghost" onClick={() => setPickerOpen(false)}>Done</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OrganizationRules;
